/**
 * @file 問1つの解説を、いまの面の上に重ねて出す対話である。
 *
 * @what: 資料 → 作品 (work_id) → 問 → 解説の順に引き、GuideBody をそのまま対話の中に出す。
 *   下に「問題ごとの解説の面で読む」の線を置き、面ごと開きたいときだけそちらへ移る。
 * @why: AI 出力から解説へ行くたびにリーダーを離れると勉強の邪魔になる (代表の判断、2026-09-09)。
 *   紐づく問の解説だけをその場で読めれば足りる。
 * @failure: 資料に作品が無い・問が無いときは1行で知らせる。解説の取得失敗も1行にする。
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchStudyProblemGuide } from "../api/studyWorks";
import { StudyDialog } from "../components/StudyDialog";
import { useStudySources } from "../hooks/useStudySources";
import { useStudyWork } from "../hooks/useStudyWork";
import { GuideBody, type GuideState } from "./ProblemGuideView";

export function ProblemGuideDialog({
  familyId,
  sourceId,
  problemId,
  onClose,
}: {
  familyId: string;
  sourceId: string;
  problemId: string;
  onClose: () => void;
}) {
  const sourcesState = useStudySources(familyId);
  const source =
    sourcesState.status === "ready"
      ? sourcesState.sources.find((s) => s.id === sourceId)
      : undefined;
  const workId = source?.work_id ?? "";
  const readerOffset = source?.reader_offset ?? 0;
  const work = useStudyWork(workId);
  const problem =
    work.status === "ready" ? work.index.problems.find((p) => p.id === problemId) : undefined;
  const hasGuide = problem?.has_guide ?? false;

  const [guide, setGuide] = useState<GuideState>({ status: "none" });
  useEffect(() => {
    if (!workId || !hasGuide) {
      setGuide({ status: "none" });
      return;
    }
    const controller = new AbortController();
    setGuide({ status: "loading" });
    fetchStudyProblemGuide(workId, problemId, controller.signal)
      .then((g) => {
        if (!controller.signal.aborted) setGuide({ status: "ready", guide: g });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load study guide", err);
        setGuide({ status: "error" });
      });
    return () => controller.abort();
  }, [workId, problemId, hasGuide]);

  const readerHref = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(sourceId)}`;
  const pageHref = (printed: number) => `${readerHref}?page=${printed + readerOffset}`;
  const guidePageHref = `${readerHref}/guides/${encodeURIComponent(problemId)}`;

  const loading =
    sourcesState.status === "loading" || work.status === "loading" || work.status === "idle";

  return (
    <StudyDialog title="問題ごとの解説" labelledBy="study-guide-dialog-title" onClose={onClose}>
      {sourcesState.status === "error" || work.status === "error" ? (
        <p className="study-error" role="alert">
          解説を読み込めませんでした。
        </p>
      ) : source !== undefined && !workId ? (
        <p className="study-muted">この資料には、問題ごとの解説がまだ結びついていません。</p>
      ) : loading && !problem ? (
        <p className="study-muted" role="status">
          読み込んでいます...
        </p>
      ) : !problem ? (
        <p className="study-muted">この問題は見つかりません。</p>
      ) : (
        <GuideBody
          problem={problem}
          guide={guide}
          pageHref={pageHref}
          titleId="study-guide-dialog-problem"
        />
      )}
      <p className="study-dialog-more">
        <Link to={guidePageHref}>問題ごとの解説の面で読む</Link>
      </p>
    </StudyDialog>
  );
}
